import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Bell, 
  BellOff,
  BellRing,
  MapPin, 
  Trophy,
  Calendar,
  Check,
  CheckCheck,
  Settings,
  Loader2,
  Smartphone
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to convert VAPID key
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export const Notifications = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [preferences, setPreferences] = useState({
    email_nearby_tournaments: true,
    email_favorite_updates: true,
    email_game_reminders: true,
    nearby_radius_miles: 50,
    preferred_location: ''
  });
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('notifications');

  useEffect(() => {
    // Check push notification support
    setPushSupported('serviceWorker' in navigator && 'PushManager' in window);
    
    // Check current push subscription
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then(registration => {
        registration.pushManager.getSubscription().then(subscription => {
          setPushEnabled(!!subscription);
        });
      });
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const [notifRes, prefsRes] = await Promise.all([
        axios.get(`${API}/notifications`),
        axios.get(`${API}/notifications/preferences`)
      ]);
      setNotifications(notifRes.data);
      setPreferences(prefsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`);
      setNotifications(notifications.map(n => 
        n.id === notificationId ? { ...n, is_read: true } : n
      ));
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`);
      setNotifications(notifications.map(n => ({ ...n, is_read: true })));
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const savePreferences = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/notifications/preferences`, preferences);
      toast.success('Preferences saved!');
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const subscribeToPush = async () => {
    if (!pushSupported) {
      toast.error('Push notifications not supported in this browser');
      return;
    }

    setSubscribing(true);
    try {
      // Get VAPID public key
      const { data } = await axios.get(`${API}/push/vapid-public-key`);
      
      // Request permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        toast.error('Notification permission denied');
        setSubscribing(false);
        return;
      }

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;
      
      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.public_key)
      });

      // Send subscription to backend
      await axios.post(`${API}/push/subscribe`, {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')))),
          auth: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth'))))
        }
      });

      setPushEnabled(true);
      toast.success('Push notifications enabled!');
    } catch (error) {
      console.error('Push subscription error:', error);
      toast.error('Failed to enable push notifications');
    } finally {
      setSubscribing(false);
    }
  };

  const unsubscribeFromPush = async () => {
    setSubscribing(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        await subscription.unsubscribe();
        await axios.delete(`${API}/push/unsubscribe?endpoint=${encodeURIComponent(subscription.endpoint)}`);
      }
      
      setPushEnabled(false);
      toast.success('Push notifications disabled');
    } catch (error) {
      console.error('Unsubscribe error:', error);
      toast.error('Failed to disable push notifications');
    } finally {
      setSubscribing(false);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'nearby_tournament': return <MapPin className="w-5 h-5 text-blue-500" />;
      case 'favorite_update': return <Trophy className="w-5 h-5 text-purple-500" />;
      case 'game_reminder': return <Calendar className="w-5 h-5 text-green-500" />;
      default: return <Bell className="w-5 h-5 text-primary" />;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="text-center py-12">
          <CardContent>
            <Bell className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
            <h3 className="text-xl font-bold mb-2">Login Required</h3>
            <p className="text-muted-foreground mb-6">
              Please login to view your notifications
            </p>
            <Link to="/login">
              <Button className="btn-primary">Login</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="notifications-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black">
            <span className="text-primary">NOTIFICATIONS</span>
          </h1>
          <p className="text-muted-foreground mt-1">
            Stay updated on tournaments and games
          </p>
        </div>
        
        {unreadCount > 0 && (
          <Badge className="bg-primary text-primary-foreground">
            {unreadCount} unread
          </Badge>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="notifications" className="gap-2">
            <Bell className="w-4 h-4" />
            Notifications
            {unreadCount > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 w-5 p-0 flex items-center justify-center">
                {unreadCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="settings" className="gap-2">
            <Settings className="w-4 h-4" />
            Settings
          </TabsTrigger>
        </TabsList>

        <TabsContent value="notifications">
          {/* Actions */}
          {notifications.length > 0 && unreadCount > 0 && (
            <div className="flex justify-end mb-4">
              <Button variant="outline" size="sm" onClick={markAllAsRead} className="gap-2">
                <CheckCheck className="w-4 h-4" />
                Mark all as read
              </Button>
            </div>
          )}

          {/* Notifications List */}
          {notifications.length > 0 ? (
            <div className="space-y-3">
              {notifications.map((notification, index) => (
                <Card 
                  key={notification.id}
                  className={`overflow-hidden transition-all ${
                    !notification.is_read ? 'border-primary/50 bg-primary/5' : ''
                  }`}
                  data-testid={`notification-${index}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <div className="mt-1">
                        {getNotificationIcon(notification.notification_type)}
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className={`font-semibold ${!notification.is_read ? 'text-primary' : ''}`}>
                              {notification.title}
                            </h3>
                            <p className="text-sm text-muted-foreground mt-1">
                              {notification.message}
                            </p>
                          </div>
                          
                          <span className="text-xs text-muted-foreground whitespace-nowrap ml-4">
                            {formatDate(notification.created_at)}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-2 mt-3">
                          {notification.tournament_id && (
                            <Link to={`/tournaments/${notification.tournament_id}`}>
                              <Button variant="outline" size="sm">
                                View Tournament
                              </Button>
                            </Link>
                          )}
                          
                          {!notification.is_read && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => markAsRead(notification.id)}
                              className="gap-1"
                            >
                              <Check className="w-4 h-4" />
                              Mark read
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="text-center py-12" data-testid="no-notifications">
              <CardContent>
                <BellOff className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
                <h3 className="text-xl font-bold mb-2">No Notifications</h3>
                <p className="text-muted-foreground">
                  You are all caught up! Check back later for updates.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="settings">
          <Card data-testid="notification-settings">
            <CardHeader>
              <CardTitle className="text-xl font-bold">Notification Preferences</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Push Notifications */}
              <div className="space-y-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <BellRing className="h-5 w-5" />
                  Push Notifications
                </h3>
                
                <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/30">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Smartphone className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">Browser Push Notifications</p>
                      <p className="text-sm text-muted-foreground">
                        {pushSupported 
                          ? 'Get instant alerts even when the app is closed'
                          : 'Not supported in this browser'}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant={pushEnabled ? 'outline' : 'default'}
                    onClick={pushEnabled ? unsubscribeFromPush : subscribeToPush}
                    disabled={!pushSupported || subscribing}
                    className="gap-2"
                    data-testid="push-toggle-btn"
                  >
                    {subscribing ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : pushEnabled ? (
                      <>
                        <BellOff className="h-4 w-4" />
                        Disable
                      </>
                    ) : (
                      <>
                        <Bell className="h-4 w-4" />
                        Enable
                      </>
                    )}
                  </Button>
                </div>
                
                {pushEnabled && (
                  <p className="text-sm text-green-600 flex items-center gap-1">
                    <Check className="h-4 w-4" />
                    Push notifications are enabled for this device
                  </p>
                )}
              </div>

              {/* Email Notifications */}
              <div className="space-y-4">
                <h3 className="font-semibold">Email Notifications</h3>
                
                <div className="flex items-center justify-between p-4 rounded-lg border">
                  <div>
                    <p className="font-medium">Nearby Tournaments</p>
                    <p className="text-sm text-muted-foreground">
                      Get notified when new tournaments are posted in your area
                    </p>
                  </div>
                  <Switch
                    checked={preferences.email_nearby_tournaments}
                    onCheckedChange={(checked) => 
                      setPreferences({ ...preferences, email_nearby_tournaments: checked })
                    }
                    data-testid="nearby-toggle"
                  />
                </div>
                
                <div className="flex items-center justify-between p-4 rounded-lg border">
                  <div>
                    <p className="font-medium">Favorite Updates</p>
                    <p className="text-sm text-muted-foreground">
                      Updates about tournaments in your watchlist
                    </p>
                  </div>
                  <Switch
                    checked={preferences.email_favorite_updates}
                    onCheckedChange={(checked) => 
                      setPreferences({ ...preferences, email_favorite_updates: checked })
                    }
                    data-testid="favorites-toggle"
                  />
                </div>
                
                <div className="flex items-center justify-between p-4 rounded-lg border">
                  <div>
                    <p className="font-medium">Game Reminders</p>
                    <p className="text-sm text-muted-foreground">
                      Reminders for upcoming games your team is playing
                    </p>
                  </div>
                  <Switch
                    checked={preferences.email_game_reminders}
                    onCheckedChange={(checked) => 
                      setPreferences({ ...preferences, email_game_reminders: checked })
                    }
                    data-testid="reminders-toggle"
                  />
                </div>
              </div>

              {/* Location Settings */}
              <div className="space-y-4">
                <h3 className="font-semibold">Location Settings</h3>
                
                <div className="space-y-2">
                  <Label htmlFor="preferred_location">Preferred Location</Label>
                  <Input
                    id="preferred_location"
                    value={preferences.preferred_location}
                    onChange={(e) => setPreferences({ ...preferences, preferred_location: e.target.value })}
                    placeholder="e.g., Dallas, TX"
                    data-testid="location-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    We will notify you about tournaments in this area
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="radius">Search Radius (miles)</Label>
                  <Input
                    id="radius"
                    type="number"
                    min="10"
                    max="500"
                    value={preferences.nearby_radius_miles}
                    onChange={(e) => setPreferences({ ...preferences, nearby_radius_miles: parseInt(e.target.value) })}
                    data-testid="radius-input"
                  />
                </div>
              </div>

              {/* Save Button */}
              <Button 
                onClick={savePreferences}
                disabled={saving}
                className="w-full btn-primary"
                data-testid="save-preferences-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Preferences'
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
